import { useEffect, useRef, useState } from 'react';
import { useToast } from '@/context/ToastContext';
import { rpcClient } from '@/api/rpc/client';
import type { AudioStreamEvent } from '@erp/rpc';

export interface UseVoiceInputOptions {
  /**
   * Called with the live partial transcript while recording, and with `null`
   * when a recording session ends. Use it to mirror the in-progress transcript
   * into the composer's input value.
   */
  onTranscript?: (text: string | null) => void;
  /**
   * Called with the final transcript when the user releases the mic. Wire this
   * to the composer's send action (it commits the spoken message).
   */
  onCommit?: (finalText: string) => void;
  /** When true, the mic is inert (e.g. while a response is streaming). */
  disabled?: boolean;
}

export interface VoiceInputControls {
  recording: boolean;
  isTranscribing: boolean;
  /** Spread onto the mic button element to drive hold-to-speak. */
  pressHandlers: {
    onMouseDown: (e: React.MouseEvent | React.TouchEvent) => void;
    onMouseUp: (e: React.MouseEvent | React.TouchEvent) => void;
    onMouseLeave: () => void;
    onTouchStart: (e: React.MouseEvent | React.TouchEvent) => void;
    onTouchEnd: (e: React.MouseEvent | React.TouchEvent) => void;
  };
}

/**
 * Push-to-talk voice capture for chat composers. Records mic audio while the
 * button is held, streams it to the transcription endpoint, surfaces the live
 * transcript via `onTranscript`, and commits the final text via `onCommit` on
 * release. Shared by the real chat composer (`ChatInput`) and the demo
 * copilots' `VoiceInputButton`, so every chat surface behaves identically.
 */
export function useVoiceInput({
  onTranscript,
  onCommit,
  disabled = false,
}: UseVoiceInputOptions): VoiceInputControls {
  const [recording, setRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const transcribeAbortRef = useRef(false);
  const currentTranscriptRef = useRef('');
  const sessionIdRef = useRef(0);
  const activeSessionRef = useRef<number | null>(null);
  const { showToast } = useToast();

  // Always reach the latest callbacks, avoiding stale closures in recorder.onstop
  // and the async transcription loop.
  const onCommitRef = useRef(onCommit);
  const onTranscriptRef = useRef(onTranscript);
  useEffect(() => {
    onCommitRef.current = onCommit;
    onTranscriptRef.current = onTranscript;
  }, [onCommit, onTranscript]);

  // Stop any active capture when the consumer unmounts.
  useEffect(() => {
    return () => {
      transcribeAbortRef.current = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleStreamEvent = (event: AudioStreamEvent, sessionId: number) => {
    if (activeSessionRef.current !== sessionId) return;

    switch (event.type) {
      case 'transcription-delta':
        currentTranscriptRef.current = event.text;
        onTranscriptRef.current?.(event.text);
        break;
      case 'complete':
        if (event.finalText) {
          currentTranscriptRef.current = event.finalText;
          onTranscriptRef.current?.(event.finalText);
        }
        setIsTranscribing(false);
        break;
      case 'error':
        console.error('[useVoiceInput] transcription error:', event.message);
        setIsTranscribing(false);
        break;
    }
  };

  const transcribeAudio = async (audioBlob: Blob, sessionId: number) => {
    if (transcribeAbortRef.current) return;

    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const chunkSize = 8192;
      let binaryString = '';
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.subarray(i, i + chunkSize);
        binaryString += String.fromCharCode(...chunk);
      }
      const base64Audio = btoa(binaryString);

      const response = await rpcClient.ai.streamingAudio.streamAudioInput({
        audioData: base64Audio,
        config: {
          sampleRate: 16000,
          channels: 1,
          enableVAD: true,
          vadSensitivity: 'medium',
          language: 'en',
        },
      });

      for await (const event of response) {
        if (
          transcribeAbortRef.current ||
          activeSessionRef.current !== sessionId
        )
          break;
        handleStreamEvent(event, sessionId);
      }
    } catch (error) {
      if (transcribeAbortRef.current) return;
      console.error('[useVoiceInput] transcription error:', error);
    } finally {
      if (
        !transcribeAbortRef.current &&
        activeSessionRef.current === sessionId
      ) {
        setIsTranscribing(false);
      }
    }
  };

  const cleanupRecording = () => {
    transcribeAbortRef.current = true;
    activeSessionRef.current = null;
    setRecording(false);
    setIsTranscribing(false);
    onTranscriptRef.current?.(null);
    currentTranscriptRef.current = '';
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // recorder may already be inactive
      }
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    chunksRef.current = [];
  };

  const startRecording = async () => {
    if (disabled || recording) return;

    setRecording(true);
    onTranscriptRef.current?.('');

    try {
      const currentSession = ++sessionIdRef.current;
      activeSessionRef.current = currentSession;
      transcribeAbortRef.current = false;
      chunksRef.current = [];
      currentTranscriptRef.current = '';

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });

      recorder.ondataavailable = (e) => {
        if (
          e.data &&
          e.data.size > 0 &&
          activeSessionRef.current === currentSession
        ) {
          chunksRef.current.push(e.data);

          // Transcribe the cumulative audio so partial text updates as the
          // user keeps speaking.
          const cumulativeBlob = new Blob(chunksRef.current, {
            type: recorder.mimeType || 'audio/webm',
          });

          if (cumulativeBlob.size > 1000) {
            setIsTranscribing(true);
            void transcribeAudio(cumulativeBlob, currentSession);
          }
        }
      };

      recorder.onstop = async () => {
        if (
          chunksRef.current.length > 0 &&
          activeSessionRef.current === currentSession
        ) {
          const finalBlob = new Blob(chunksRef.current, {
            type: recorder.mimeType || 'audio/webm',
          });

          if (finalBlob.size > 1000) {
            setIsTranscribing(true);
            await transcribeAudio(finalBlob, currentSession);
          }
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        if (activeSessionRef.current === currentSession) {
          const finalText = currentTranscriptRef.current.trim();
          if (finalText) {
            onCommitRef.current?.(finalText);
          }

          activeSessionRef.current = null;
          onTranscriptRef.current?.(null);
          currentTranscriptRef.current = '';
        }
      };

      const CHUNK_INTERVAL_MS = 1500;
      recorder.start(CHUNK_INTERVAL_MS);
      mediaRecorderRef.current = recorder;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      showToast({
        title: 'Microphone Error',
        message:
          'Could not access microphone. Please check your browser permissions.',
        color: 'error',
      });
      cleanupRecording();
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setRecording(false);
  };

  const handleMicPressStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!disabled && !recording) {
      void startRecording();
    }
  };

  const handleMicPressEnd = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (recording) {
      stopRecording();
    }
  };

  const handleMicLeave = () => {
    if (recording) {
      stopRecording();
    }
  };

  return {
    recording,
    isTranscribing,
    pressHandlers: {
      onMouseDown: handleMicPressStart,
      onMouseUp: handleMicPressEnd,
      onMouseLeave: handleMicLeave,
      onTouchStart: handleMicPressStart,
      onTouchEnd: handleMicPressEnd,
    },
  };
}
